import React, { useState, useEffect, useRef } from 'react';
import { 
  Box,
  BoxProps,
  Typography, 
  Button, 
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TablePagination,
  Snackbar,
  Alert
} from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useNavigate } from 'react-router-dom';
import WorkIcon from '@mui/icons-material/Work';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChatIcon from '@mui/icons-material/Chat';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SentimentAnalysisIcon from '@mui/icons-material/Psychology';
import { environment } from '../../environments/environment';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import botIcon from '../../assets/Chatbot.svg';

// Interfaces
interface InputField {
  key: string;
  label: string;
  type: string;
  value: string | string[];
  info: string;
  options?: Array<{ id: string; label: string }>;
}

interface JobDescription {
  objectType: string;
  docUID: string;
  entries: Array<{
    key: string;
    value: string | string[];
    label: string;
  }>;
}

interface Resume {
  objectType: string;
  docUID: string;
  entries: Array<{
    key: string;
    label: string;
    value: any;
  }>;
}

interface ProjectExperience {
  endDate: string;
  projectName: string;
  skills: string[];
  startDate: string;
  organisationName: string;
  roles: string[];
}

interface Education {
  score: string | number;
  degree: string;
  instituteName: string;
  passingYear: number;
}

interface Certification {
  issuingOrganization: string;
  issueDate: string;
  certificationName: string;
}

interface ChatMessage {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface MessageBubbleProps extends BoxProps {
  sender: 'user' | 'bot';
}

const FullScreenContainer = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '#FFFFFF',
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  padding: '24px',
  height: '100vh',
  overflow: 'hidden',
  '& > *': {
    flexShrink: 0
  }
});

const MessageBubble = styled(Box)<MessageBubbleProps>(({ theme, sender }) => ({
  padding: '12px 16px',
  borderRadius: sender === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
  maxWidth: '85%',
  wordBreak: 'break-word',
  fontSize: '0.875rem',
  lineHeight: '1.5',
  backgroundColor: sender === 'user' ? '#E3F2FD' : '#F5F5F5',
  color: sender === 'user' ? '#FFFFFF' : '#131F32',
  alignSelf: sender === 'user' ? 'flex-end' : 'flex-start',
  marginBottom: '12px',
  boxShadow: sender === 'user' 
    ? '0 2px 8px rgba(21, 157, 255, 0.15)' 
    : '0 2px 8px rgba(0, 0, 0, 0.05)',
  position: 'relative',
  '&:last-child': {
    marginBottom: 0
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    [sender === 'user' ? 'left' : 'right']: '12px',
    width: '12px',
    height: '12px',
    backgroundColor: sender === 'user' ? '#159DFF' : '#F8F9FA',
    clipPath: sender === 'user' 
      ? 'polygon(0 0, 100% 100%, 0 100%)' 
      : 'polygon(0 0, 100% 0, 100% 100%)',
  }
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  border: '1px solid #E0E0E0',
  borderRadius: '12px !important',
  boxShadow: 'none',
  marginBottom: '16px',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: '8px 0',
  },
  '&:hover': {
    borderColor: '#159DFF',
    backgroundColor: 'rgba(21, 157, 255, 0.02)',
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: '16px 24px',
  '& .MuiAccordionSummary-content': {
    margin: '0',
  },
  '&.Mui-expanded': {
    borderBottom: '1px solid #E0E0E0',
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: '24px',
  backgroundColor: '#FAFAFA',
}));

// Add formatMessageContent function
const formatMessageContent = (message: ChatMessage) => {
  try {
  console.log(message.text);
  
    // If the message is a plain string and not JSON, format it as regular text
   
    // Parse the message if it's a string, or use it directly if it's already an object
    const data = typeof message.text === 'string' ? JSON.parse(message.text) : message.text;

    // If data is an array, wrap it in an object with a data property
    const formattedData = Array.isArray(data) ? { data } : data;
    
    if (formattedData.data) {
      return (
        <Box sx={{ width: '100%' }}>
          {formattedData.data.map((item: any, index: number) => {
            if (item.objectType === 'DICTIONARY') {
              const entries = item.entries || [];
              const title = entries.find((e: any) => e.key === 'title')?.value?.toString() || 'Resume Details';
              const briefDescription = entries.find((e: any) => e.key === 'briefDescription')?.value?.toString() || '';
              const topicsEntry = entries.find((e: any) => e.key === 'topics')?.value;
              const highlightsEntry = entries.find((e: any) => e.key === 'highlights')?.value;
              const detailedDescription = entries.find((e: any) => e.key === 'detailedDescription')?.value?.toString() || '';

              // Ensure topics and highlights are string arrays
              const topics = Array.isArray(topicsEntry) ? topicsEntry.map(t => t.toString()) : [];
              const highlights = Array.isArray(highlightsEntry) ? highlightsEntry.map(h => h.toString()) : [];

              return (
                <StyledAccordion key={item.docUID}
                sx={{
                  mb: 1,
                  borderRadius: '8px !important',
                  '&:before': {
                    display: 'none',
                  },
                  '&.Mui-expanded': {
                    margin: '0 0 8px 0',
                  }
                }}
        
>
                  <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography  sx={{ fontWeight: 600, color: '#1A2027', fontSize: '0.75rem' }}>
                        {title}
                      </Typography>
                     
                    </Box>
                  </StyledAccordionSummary>
                  <StyledAccordionDetails>

                   {briefDescription && (
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '0.75rem' }}>
                          {briefDescription}
                        </Typography>
                      )}
                    {topics.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A2027', mb: 1, fontSize: '0.75rem' }}>
                          Topics
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {topics.map((topic, idx) => (
                            <Chip
                              key={idx}
                              label={topic}
                              sx={{
                                backgroundColor: '#E3F2FD',
                                color: '#1976D2',
                                '&:hover': {
                                  backgroundColor: '#BBDEFB'
                                },
                                fontSize: '0.625rem',
                                height: '20px',
                                '& .MuiChip-label': {
                                  padding: '0 6px'
                                }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {highlights.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A2027', mb: 1, fontSize: '0.75rem' }}>
                          Highlights
                        </Typography>
                        {highlights.map((highlight, idx) => (
                          <Typography key={idx} variant="body2" sx={{ 
                            color: '#666', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            mb: 0.5,
                            fontSize: '0.75rem'
                          }}>
                            <Box sx={{ 
                              width: 4, 
                              height: 4, 
                              borderRadius: '50%', 
                              backgroundColor: '#1976D2', 
                              flexShrink: 0 
                            }} />
                            {highlight}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    {detailedDescription && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1A2027', mb: 1, fontSize: '0.75rem' }}>
                          Detailed Description
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}>
                          {detailedDescription}
                        </Typography>
                      </Box>
                    )}
                  </StyledAccordionDetails>
                </StyledAccordion>
              );
            } else if (item.objectType === 'PARAGRAPH') {
              return (
                <Typography key={index} variant="body2" sx={{ 
                  whiteSpace: 'pre-wrap', 
                  mb: 2,
                  color: '#666',
                  lineHeight: 1.6,
                  fontSize: '0.75rem'
                }}>
                  {item.value?.toString()}
                </Typography>
              );
            } 
            return null;
          })}
        </Box>
      );
    }
    // If no data property is found, format as regular text
    return (
      <Typography variant="body2" sx={{ 
        whiteSpace: 'pre-wrap', 
        color: '#666',
        lineHeight: 1.6,
        fontSize: '0.75rem'
      }}>
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text)}
      </Typography>
    );
  } catch (error) {
    // If there's an error parsing the message, display it as regular text
    return (
      <Typography variant="body2" sx={{ 
        whiteSpace: 'pre-wrap', 
        color: '#666',
        lineHeight: 1.6,
        fontSize: '0.75rem'
      }}>
        {typeof message.text === 'string' ? message.text : JSON.stringify(message.text)}
      </Typography>
    );
  }
};

const ResumeRanking = () => {
  // State for job descriptions
  const [jobDescriptions, setJobDescriptions] = useState<JobDescription[]>([]);
  const [selectedJob, setSelectedJob] = useState<JobDescription | null>(null);
  const [jobSearchFields, setJobSearchFields] = useState<InputField[]>([]);
  const [jobSearchQuery, setJobSearchQuery] = useState({
    topic: '',
    keywords: [] as string[],
    contentType: 'JOB_DESCRIPTION'
  });
  const navigate = useNavigate();
  // State for resumes
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [selectedResume, setSelectedResume] = useState<Resume | null>(null);
  const [resumeUploadDialog, setResumeUploadDialog] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 1,
      text: "Hello! I'm your AI assistant. How can I help you with job matching and resume ranking?",
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [suggestedQuestions, setSuggestedQuestions] = useState([
    { text: "What are the key requirements for this role?", icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
    { text: "Analyze candidate experience", icon: <ShowChartIcon sx={{ fontSize: 16 }} /> },
    { text: "Compare top candidates", icon: <TrendingDownIcon sx={{ fontSize: 16 }} /> },
    { text: "Evaluate technical skills", icon: <MonetizationOnIcon sx={{ fontSize: 16 }} /> },
    { text: "Assess cultural fit", icon: <SentimentAnalysisIcon sx={{ fontSize: 16 }} /> }
  ]);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  // Add new state for job creation
  const [newJobDialog, setNewJobDialog] = useState(false);
  const [newJobContent, setNewJobContent] = useState('');
  const [isSubmittingJob, setIsSubmittingJob] = useState(false);

  // Add page state
  const [currentPage, setCurrentPage] = useState<'jobs' | 'matching'>('jobs');

  // Add job pagination state
  const [jobPage, setJobPage] = useState(0);
  const [jobsPerPage] = useState(3);

  // Add resume pagination state
  const [resumePage, setResumePage] = useState(0);
  const [resumesPerPage] = useState(3);

  // Add new state for notifications
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [jobLoading, setJobLoading] = useState(false);

  useEffect(() => {
    fetchJobSearchFields();
  }, []);
useEffect(() => {setJobPage(0);setResumePage(0)},[currentPage]);
  // Fetch input fields for job search
  const fetchJobSearchFields = async () => {
    try {
      const response = await axios.post(
        `${environment.apiUrl}/agent/getPayload`,
        {
          data: {},
          agent: {
            code: "QUERY_JOB_DESCRIPTIONS"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          }
        }
      );
      setJobSearchFields(response.data.data.primaryInput);
    } catch (error:any) {
      console.error('Error fetching job search fields:', error);
      if (
        error?.response?.data?.error === 'jwt malformed' ||
        error?.response?.data?.error === 'jwt expired'
      ) {
        console.log(error?.response?.data?.error);
        navigate('/login');
      }
    }
  };
  useEffect(() => {
   handleJobSearch();
  }, []);

  // Search for jobs
  const handleJobSearch = async () => {
    setSearchLoading(true);
    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: jobSearchQuery,
          agent: {
            code: "QUERY_JOB_DESCRIPTIONS"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          }
        }
      );
      setJobDescriptions(response.data.data);
    } catch (error:any) {
      console.error('Error searching jobs:', error);
      if (
        error?.response?.data?.error === 'jwt malformed' ||
        error?.response?.data?.error === 'jwt expired'
      ) {
        console.log(error?.response?.data?.error);
        navigate('/login');
      }
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      setUploadLoading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('token', `Bearer ${localStorage.getItem('token')}`);
      formData.append('type', 'resume');

      try {
        const uploadResponse = await axios.post(
          `${environment.apiUrl}/account/uploadFileForOrg`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            }
          }
        );

        if (uploadResponse.data.status === 'success') {
          await axios.post(
            `${environment.controlplaneUrl}/agent/flow/execute`,
            {
              data: {
                filePath: uploadResponse.data.path,
                jobPosition: selectedJob ? [selectedJob.docUID] : [],
                contentType: "TALENT_PROFILE"
              },
              agent: {
                code: "INDEX_CANDIDATE_RESUME"
              }
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json',
              }
            }
          );

          setSuccess('Resume uploaded and indexed successfully');
          setResumeUploadDialog(false);
          if (selectedJob) {
            handleResumeSearch();
          }
        }
      } catch (error:any) {
        console.error('Error uploading resume:', error);
        if (
          error?.response?.data?.error === 'jwt malformed' ||
          error?.response?.data?.error === 'jwt expired'
        ) {
          console.log(error?.response?.data?.error);
          navigate('/login');
        }
        setError('Failed to upload resume. Please try again.');
      } finally {
        setUploadLoading(false);
      }
    }
  };
useEffect(() => {
  if (currentPage === 'matching') {
    if (selectedJob) {
      handleResumeSearch();
    }
  }
}, [currentPage]);
  // Handle resume search
  const handleResumeSearch = async () => {
    if (!selectedJob) return;
    setJobLoading(true);
    setSearchLoading(true);
    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            docUID: selectedJob.docUID,
            jobPosition: selectedJob.docUID,
            relevantExperience: [0, 600],
            contentType: "TALENT_PROFILES"
          },
          agent: {
            code: "MATCH_AND_RANK_CANDIDATE_RESUME"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          }
        }
      );
      setResumes(response.data.data);
      setSuccess('Resumes fetched successfully');
      setSearchLoading(false);
    } catch (error:any) {
      console.error('Error searching resumes:', error);
      if (
        error?.response?.data?.error === 'jwt malformed' ||
        error?.response?.data?.error === 'jwt expired'
      ) {
        console.log(error?.response?.data?.error);
        navigate('/login');
      }
    } finally {
      setJobLoading(false);
      setSearchLoading(false);
    }
  };

  // Helper function to safely get string value
  const getStringValue = (value: any) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  // Update handleSendMessage to include typing indicator and suggested questions
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    const userMessage: ChatMessage = {
      id: Math.floor(Date.now()),
      text: newMessage.trim(),
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsTyping(true);

    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            query: newMessage.trim(),
            contentType: "JOB_DESCRIPTION",
            docUID: selectedJob?.docUID
          },
          agent: {
            code: selectedJob?.docUID ? "QUERY_JOB_AND_RESPOND" : "ANALYZE_JOBS"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );

      const botMessage: ChatMessage = {
        id: Math.floor(Date.now()),
        text: getStringValue(response.data.data),
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);

      const followupItem = response.data.data.find((item: any) => 
        item.objectType === 'MULTITEXTOPTIONS'
      );

      if (followupItem) {
        const icons = [
          <TrendingUpIcon sx={{ fontSize: 16 }} />,
          <ShowChartIcon sx={{ fontSize: 16 }} />,
          <TrendingDownIcon sx={{ fontSize: 16 }} />,
          <MonetizationOnIcon sx={{ fontSize: 16 }} />,
          <SentimentAnalysisIcon sx={{ fontSize: 16 }} />
        ];

        const newSuggestedQuestions = followupItem.value.map((question: string, index: number) => ({
          text: question,
          icon: icons[index % icons.length]
        }));

        console.log('Setting new suggested questions:', newSuggestedQuestions);
        setSuggestedQuestions(newSuggestedQuestions);
      }
    } catch (error:any) {
      console.error('Error in chat:', error);
     
      const errorMessage: ChatMessage = {
        id: Math.floor(Date.now()),
        text: 'I apologize, but I encountered an error. Please try again.',
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };
   const handleSendMessageResume = async () => {
    if (!newMessage.trim()) return;

    const userMessage: ChatMessage = {
      id: Math.floor(Date.now()),
      text: newMessage.trim(),
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsTyping(true);

    try {
      const response = await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            docUID: selectedResume?.docUID,
            contentType: "TALENT_PROFILE",
            query: newMessage.trim()
          },
          agent: {
            code: selectedResume?.docUID ? "QUERY_RESUME_AND_RESPOND" : "ANALYZE_JOBS"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          }
        }
      );

      const botMessage: ChatMessage = {
        id: Math.floor(Date.now()),
        text: getStringValue(response.data.data),
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);

      // Update suggested questions based on context
      const followupItem = response.data.data.find((item: any) => 
        item.objectType === 'MULTITEXTOPTIONS'
      );

      if (followupItem) {
        const icons = [
          <TrendingUpIcon sx={{ fontSize: 16 }} />,
          <ShowChartIcon sx={{ fontSize: 16 }} />,
          <TrendingDownIcon sx={{ fontSize: 16 }} />,
          <MonetizationOnIcon sx={{ fontSize: 16 }} />,
          <SentimentAnalysisIcon sx={{ fontSize: 16 }} />
        ];

        const newSuggestedQuestions = followupItem.value.map((question: string, index: number) => ({
          text: question,
          icon: icons[index % icons.length]
        }));

        console.log('Setting new suggested questions:', newSuggestedQuestions);
        setSuggestedQuestions(newSuggestedQuestions);
      }
    } catch (error:any) {
      console.error('Error in chat:', error);
      if (
        error?.response?.data?.error === 'jwt malformed' ||
        error?.response?.data?.error === 'jwt expired'
      ) {
        console.log(error?.response?.data?.error);
        navigate('/login');
      }
      const errorMessage: ChatMessage = {
        id: Math.floor(Date.now()),
        text: 'I apologize, but I encountered an error. Please try again.',
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };


  // Add handler for job submission
  const handleSubmitNewJob = async () => {
    if (!newJobContent.trim()) return;
    setJobLoading(true);
    try {
      await axios.post(
        `${environment.controlplaneUrl}/agent/flow/execute`,
        {
          data: {
            contentText: newJobContent,
            contentType: "JOB_DESCRIPTION"
          },
          agent: {
            code: "INDEX_JOB_DESCRIPTION"
          }
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          }
        }
      );

      setSuccess('Job created successfully');
      handleJobSearch();
      setNewJobDialog(false);
      setNewJobContent('');
    } catch (error:any) {
      console.error('Error submitting new job:', error);
      if (
        error?.response?.data?.error === 'jwt malformed' ||
        error?.response?.data?.error === 'jwt expired'
      ) {
        console.log(error?.response?.data?.error);
        navigate('/login');
      }
    } finally {
      setJobLoading(false);
    }
  };

  // Handle job selection and page change
  const handleJobSelect = (job: JobDescription) => {
    setSelectedJob(job);
    setCurrentPage('matching');
  };

  // Handle back to jobs
  const handleBackToJobs = () => {
    setCurrentPage('jobs');
  };

  // Add handleJobPageChange function
  const handleJobPageChange = (event: unknown, newPage: number) => {
    setJobPage(newPage);
  };

  // Add handleResumePageChange function
  const handleResumePageChange = (event: unknown, newPage: number) => {
    setResumePage(newPage);
  };

  // Add this at the end of the component, before the return statement
  const handleCloseSnackbar = (type: 'error' | 'success') => {
    if (type === 'error') {
      setError(null);
    } else {
      setSuccess(null);
    }
  };

  return (
    <Box sx={{ padding: '40px' }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between',
        // marginBottom: '32px'
      }}>


<Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '16px',
        marginBottom: '24px'
      }}>
        <Box sx={{
          height: '48px',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
         <Button
          startIcon={<ArrowBackIosIcon sx={{ fontSize: 18 }} />}
                          onClick={() =>{ 
                  if (currentPage === 'matching') {
                    handleBackToJobs();
                  } else {
                    // Check if we have a stored navigation context
                    const context = sessionStorage.getItem('appsNavigationContext');
                    if (context && context.startsWith('industry:')) {
                      // Navigate back to Apps with the stored context
                      navigate('/Apps');
                    } else {
                      // Default navigation to Apps
                      navigate('/Apps');
                    }
                  }
                }}
          sx={{
            color: '#159DFF',
            fontWeight: 600,
            fontSize: '0.875rem',
            '&:hover': {
              backgroundColor: '#F3E5F5'
            }
          }}
        >
          Back
        </Button>
          <WorkIcon sx={{ fontSize: 24, color: '#159DFF', marginLeft: '24px' }} />
        </Box>
        <Typography variant="h5" sx={{ color: '#131F32', fontWeight: 600, fontSize: '1.25rem' }}>
        {currentPage === 'jobs' ? 'Job Management' : 'Resume Matching'}
        </Typography>
      </Box>
    
      </Box>

      {currentPage === 'jobs' ? (
        <Box sx={{ display: 'flex', gap: '24px' }}>
          {/* Left Panel - Jobs List */}
          <Box sx={{ flex: 2 }}>
            <Paper sx={{ p: 3,  height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 3 
              }}>
                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>Job List</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                  onClick={() => setNewJobDialog(true)}
                  sx={{
                    backgroundColor: '#159DFF',
                    fontSize: '0.75rem',
                    padding: '6px 12px',
                    '&:hover': {
                      backgroundColor: '#0B7AD1'
                    }
                  }}
                >
                  Add Job
                </Button>
              </Box>

              {isSearching ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ 
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: '300px',
                    // maxHeight: 'calc(100vh - 400px)'
                  }}>
                    {jobDescriptions
                      .slice(jobPage * jobsPerPage, (jobPage + 1) * jobsPerPage)
                      .map((job) => {
                      const jobTitle = job.entries.find(entry => entry.key === 'jobTitle')?.value || 'Untitled Position';
                      const aboutRole = job.entries.find(entry => entry.key === 'aboutRole')?.value || '';
                      const responsibilities = job.entries.find(entry => entry.key === 'responsibilities')?.value || [];
                      const mandatoryRequirements = job.entries.find(entry => entry.key === 'mandatoryRequirements')?.value || [];
                      const preferredRequirements = job.entries.find(entry => entry.key === 'preferredRequirements')?.value || [];
                      const locations = job.entries.find(entry => entry.key === 'locations')?.value || [];
                      const aboutCompany = job.entries.find(entry => entry.key === 'aboutCompany')?.value || '';

                      return (
                        <StyledAccordion
                          key={job.docUID}
                          expanded={selectedJob?.docUID === job.docUID}
                          onChange={(event, expanded) => {
                            if (expanded) {
                              setSelectedJob(job);
                              setMessages([
                                {
                                  id: 1,
                                  text: "Hello! I'm your AI assistant. How can I help you analyze this job and find matching candidates?",
                                  sender: 'bot',
                                  timestamp: new Date(),
                                }
                              ]);
                              setNewMessage('');
                              setSuggestedQuestions([
                                { text: "What are the key requirements?", icon: <TrendingUpIcon sx={{ fontSize: 16 }} /> },
                                { text: "Analyze responsibilities", icon: <ShowChartIcon sx={{ fontSize: 16 }} /> },
                                { text: "Find best candidates", icon: <TrendingDownIcon sx={{ fontSize: 16 }} /> },
                                { text: "Required experience", icon: <MonetizationOnIcon sx={{ fontSize: 16 }} /> },
                                { text: "Technical skills needed", icon: <SentimentAnalysisIcon sx={{ fontSize: 16 }} /> }
                              ]);
                            } else {
                              setSelectedJob(null);
                            }
                          }}
                        >
                          <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography 
                                  variant="subtitle1" 
                                  sx={{ 
                                    fontWeight: 600,
                                    color: '#131F32',
                                    marginBottom: '4px',
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {jobTitle}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    color: '#64748B',
                                    marginBottom: '8px',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  {aboutRole}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {Array.isArray(locations) && locations.map((location: string, idx: number) => (
                                    <Chip
                                      key={idx}
                                      label={location}
                                      size="small"
                                      sx={{
                                        backgroundColor: '#E3F2FD',
                                        color: '#159DFF',
                                        fontSize: '0.625rem',
                                        height: '20px',
                                        '& .MuiChip-label': {
                                          padding: '0 6px'
                                        }
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                              <Button
                                variant="contained"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleJobSelect(job);
                                }}
                                startIcon={<TextSnippetIcon />}
                                sx={{
                                  ml: 2,
                                  backgroundColor: '#FFFFFF',
                                  color: '#159DFF',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  padding: '6px 16px',
                                  borderRadius: '6px',
                                  textTransform: 'none',
                                  boxShadow: '0 2px 8px rgba(21, 157, 255, 0.25)',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    color: '#0B7AD1',
                                    backgroundColor: '#FFFFFF',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(21, 157, 255, 0.35)',
                                  },
                                  '&:active': {
                                    transform: 'translateY(0)',
                                    backgroundColor: '#FFFFFF',
                                    boxShadow: '0 2px 4px rgba(21, 157, 255, 0.25)',
                                  },
                                  '& .MuiButton-startIcon': {
                                    marginRight: '4px',
                                    '& .MuiSvgIcon-root': {
                                      fontSize: '1rem',
                                    }
                                  }
                                }}
                              >
                                View Matching Resumes
                              </Button>
                            </Box>
                          </StyledAccordionSummary>
                          <StyledAccordionDetails>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {aboutCompany && (
                                <Box>
                                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#131F32', fontSize: '0.75rem', fontWeight: 600 }}>
                                    About Company
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.75rem' }}>
                                    {aboutCompany}
                                  </Typography>
                                </Box>
                              )}
                              
                              {responsibilities.length > 0 && (
                                <Box>
                                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#131F32', fontSize: '0.75rem', fontWeight: 600 }}>
                                    Key Responsibilities
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {Array.isArray(responsibilities) && responsibilities.map((resp: string, idx: number) => (
                                      <Typography key={idx} variant="body2" sx={{ color: '#64748B', fontSize: '0.75rem' }}>
                                        • {resp}
                                      </Typography>
                                    ))}
                                  </Box>
                                </Box>
                              )}

                              {mandatoryRequirements.length > 0 && (
                                <Box>
                                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#131F32', fontSize: '0.75rem', fontWeight: 600 }}>
                                    Mandatory Requirements
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {Array.isArray(mandatoryRequirements) && mandatoryRequirements.map((req: string, idx: number) => (
                                      <Chip
                                        key={idx}
                                        label={req}
                                        size="small"
                                        sx={{
                                          backgroundColor: '#E3F2FD',
                                          color: '#159DFF',
                                          fontSize: '0.625rem',
                                          height: '20px'
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}

                              {preferredRequirements.length > 0 && (
                                <Box>
                                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#131F32', fontSize: '0.75rem', fontWeight: 600 }}>
                                    Preferred Requirements
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {Array.isArray(preferredRequirements) && preferredRequirements.map((req: string, idx: number) => (
                                      <Chip
                                        key={idx}
                                        label={req}
                                        size="small"
                                        sx={{
                                          backgroundColor: '#F5F5F5',
                                          color: '#64748B',
                                          fontSize: '0.625rem',
                                          height: '20px'
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </StyledAccordionDetails>
                        </StyledAccordion>
                      );
                    })}
                  </Box>
                  
                  {/* Add Pagination Controls */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center',
                    pt: 2,
                    borderTop: '1px solid #E0E0E0',
                    mt: 'auto'
                  }}>
                    <TablePagination
                      component="div"
                      count={jobDescriptions.length}
                      page={jobPage}
                      onPageChange={handleJobPageChange}
                      rowsPerPage={jobsPerPage}
                      rowsPerPageOptions={[3]}
                      sx={{
                        marginTop: '-14px',
                        '.MuiTablePagination-toolbar': {
                          minHeight: '48px',
                        },
                        '.MuiTablePagination-displayedRows': {
                          fontSize: '0.875rem',
                        },
                        '.MuiTablePagination-selectLabel': {
                          fontSize: '0.875rem',
                        }
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>

          {/* Right Panel - Chat Interface */}
          <Box sx={{ flex: 1 }}>
            <Paper sx={{ 
              p: 3, 
              borderRadius: '16px', 
              height: 'calc(100vh - 180px)',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Chat Header */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                mb: 3,
                pb: 2,
                borderBottom: '1px solid #E0E0E0',
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    border: '2px solid #fff',
                    backgroundColor: '#F8F9FA'
                  }}>
                    <img src={botIcon} alt="bot" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#1A2027' }}>Job Assistant</Typography>
                    <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.75rem' }}>
                      {selectedJob ? 'Analyzing selected job' : 'Browse and analyze jobs'}
                    </Typography>
                  </Box>
                </Box>
            
              </Box>

              {/* Messages */}
              <Box sx={{ 
                flex: 1, 
                overflowY: 'auto',
                mb: 3,
                p: 2,
                backgroundColor: '#F8F9FA',
                borderRadius: '12px',
                border: '1px solid #E0E0E0'
              }}>
                {messages.map((message) => (
                  <MessageBubble key={message.id} sender={message.sender}>
                    <Box sx={{ position: 'relative' }}>
                      {formatMessageContent(message)}
                      <Typography variant="caption" sx={{ 
                        display: 'block',
                        mt: 1,
                        color: message.sender === 'user' ? 'rgba(255,255,255,0.8)' : '#64748B',
                        fontSize: '0.625rem',
                        textAlign: message.sender === 'user' ? 'right' : 'left'
                      }}>
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                  </MessageBubble>
                ))}
                {isTyping && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5,
                    p: '8px 16px',
                    borderRadius: '12px',
                    backgroundColor: 'rgba(21, 157, 255, 0.1)',
                    width: 'fit-content',
                    marginLeft: '16px',
                    marginBottom: '8px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    border: '1px solid #E0E0E0',
                    opacity: 0,
                    animation: 'fadeIn 0.3s ease-in-out forwards',
                    '@keyframes fadeIn': {
                      from: { opacity: 0, transform: 'translateY(10px)' },
                      to: { opacity: 1, transform: 'translateY(0)' }
                    }
                  }}>
                    {/* Animated Dots */}
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 0.5,
                      ml: 0.5
                    }}>
                      <Box sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#159DFF',
                        animation: 'pulse 1.4s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                          '40%': { opacity: 1, transform: 'scale(1)' }
                        }
                      }} />
                      <Box sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#159DFF',
                        animation: 'pulse 1.4s ease-in-out infinite 0.2s',
                        '@keyframes pulse': {
                          '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                          '40%': { opacity: 1, transform: 'scale(1)' }
                        }
                      }} />
                      <Box sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#159DFF',
                        animation: 'pulse 1.4s ease-in-out infinite 0.4s',
                        '@keyframes pulse': {
                          '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                          '40%': { opacity: 1, transform: 'scale(1)' }
                        }
                      }} />
                    </Box>
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Suggested Questions */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 0.5
                }}>
                  {suggestedQuestions.map((question, index) => (
                    <Chip
                      key={index}
                      label={question.text}
                      icon={question.icon}
                      onClick={() => {
                        setNewMessage(question.text);
                        handleSendMessage();
                      }}
                      sx={{
                        backgroundColor: '#F8F9FA',
                        color: '#1A2027',
                        fontSize: '0.75rem',
                        height: '28px',
                        fontWeight: 500,
                        border: '1px solid #E0E0E0',
                        '& .MuiChip-icon': {
                          fontSize: '14px',
                          color: '#159DFF'
                        },
                        '&:hover': {
                          backgroundColor: '#F0F4F8',
                          borderColor: '#159DFF',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Input */}
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                p: 2,
                borderTop: '1px solid #E0E0E0',
                backgroundColor: '#FFFFFF'
              }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Type your message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  multiline
                  maxRows={3}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      backgroundColor: '#FFFFFF',
                      fontSize: '0.875rem',
                      border: '1px solid #E0E0E0',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#159DFF',
                        backgroundColor: '#FFFFFF'
                      },
                      '&.Mui-focused': {
                        borderColor: '#159DFF',
                        boxShadow: '0 0 0 2px rgba(21, 157, 255, 0.1)'
                      }
                    }
                  }}
                />
                <IconButton 
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  size="small"
                  sx={{
                    backgroundColor: '#159DFF',
                    color: 'white',
                    width: '32px',
                    height: '32px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: '#0B7AD1',
                      transform: 'scale(1.05)'
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#E3F2FD',
                      color: '#A1A1A1'
                    }
                  }}
                >
                  <SendIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Box>
            </Paper>
          </Box>
        </Box>
      ) : (
        // Matching Page
        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Paper sx={{ 
              p: 3, 
              borderRadius: '12px',
              minHeight: '600px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>Matching Resumes</Typography>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setResumeUploadDialog(true)}
                  sx={{ borderColor: '#159DFF', color: '#159DFF' }}
                >
                  Upload Resume
                </Button>
              </Box>

              {/* <Button
                fullWidth
                variant="contained"
                onClick={handleResumeSearch}
                disabled={isLoading}
                sx={{
                  mb: 3,
                  backgroundColor: '#159DFF',
                  '&:hover': { backgroundColor: '#0B7AD1' }
                }}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Find Matching Resumes'}
              </Button> */}

              {resumes
                ?.slice(resumePage * resumesPerPage, (resumePage + 1) * resumesPerPage)
                .map((resume) => {
                // Helper function to safely get string value
                const getStringValue = (value: any) => {
                  if (value === null || value === undefined) return '';
                  if (typeof value === 'string') return value;
                  if (typeof value === 'object') return JSON.stringify(value);
                  return String(value);
                };

                const name = getStringValue(resume.entries.find(e => e.key === 'name')?.value) || 'Unknown Candidate';
                const email = getStringValue(resume.entries.find(e => e.key === 'email')?.value) || 'No email provided';
                const careerDescription = getStringValue(resume.entries.find(e => e.key === 'careerDescription')?.value) || '';
                const technicalSkills = (resume.entries.find(e => e.key === 'mostRelevantTechnicalAndOperationalSkills')?.value || []) as string[];
                const businessSkills = (resume.entries.find(e => e.key === 'mostRelevantManagementAndBusinessSkills')?.value || []) as string[];
                const projectExperiences = (resume.entries.find(e => e.key === 'projectExperiences')?.value || []) as ProjectExperience[];
                const educationalQualifications = (resume.entries.find(e => e.key === 'educationalQualifications')?.value || []) as Education[];
                const certifications = (resume.entries.find(e => e.key === 'certifications')?.value || []) as Certification[];

                return (
                  <Accordion
                    key={resume.docUID}
                    expanded={selectedResume?.docUID === resume.docUID}
                    onChange={() => setSelectedResume(selectedResume?.docUID === resume.docUID ? null : resume)}
                    sx={{
                      mb: 2,
                      '&:before': { display: 'none' },
                      boxShadow: 'none',
                      border: '1px solid #E0E0E0',
                      '&:hover': { borderColor: '#159DFF' }
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {email}
                        </Typography>
                        {careerDescription && careerDescription !== 'Na' && (
                          <Typography variant="body2" sx={{ mt: 1, color: '#64748B', fontSize: '0.75rem' }}>
                            {careerDescription}
                          </Typography>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {(technicalSkills.length > 0 || businessSkills.length > 0) && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Skills</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {[...technicalSkills, ...businessSkills]
                              .filter(skill => skill && typeof skill === 'string' && skill.trim() !== '')
                              .map((skill, idx) => (
                                <Chip
                                  key={idx}
                                  label={skill}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#E3F2FD',
                                    color: '#159DFF',
                                    fontSize: '0.625rem'
                                  }}
                                />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {projectExperiences.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Experience</Typography>
                          {projectExperiences.map((exp, idx) => (
                            <Box key={idx} sx={{ mb: 2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {exp.organisationName || 'Unknown Organization'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {exp.roles?.filter(Boolean).join(', ') || 'No role specified'} • 
                                {exp.startDate && exp.startDate !== 'Na' ? ` ${exp.startDate}` : ' N/A'} - 
                                {exp.endDate && exp.endDate !== 'Na' ? ` ${exp.endDate}` : ' Present'}
                              </Typography>
                              {exp.skills && exp.skills.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                  {exp.skills
                                    .filter(skill => skill && typeof skill === 'string' && skill.trim() !== '')
                                    .map((skill, skillIdx) => (
                                      <Chip
                                        key={skillIdx}
                                        label={skill}
                                        size="small"
                                        sx={{
                                          height: '20px',
                                          fontSize: '0.625rem',
                                          backgroundColor: '#F5F5F5'
                                        }}
                                      />
                                    ))}
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}

                      {educationalQualifications.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Education</Typography>
                          {educationalQualifications.map((edu, idx) => (
                            <Box key={idx} sx={{ mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {edu.degree || 'Degree not specified'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {edu.instituteName || 'Institution not specified'} • {edu.passingYear || 'Year not specified'}
                                {edu.score && edu.score !== 'Na' ? ` • Score: ${edu.score}` : ''}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {certifications.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Certifications</Typography>
                          {certifications.map((cert, idx) => (
                            <Box key={idx} sx={{ mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {cert.certificationName || 'Certification not specified'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {cert.issuingOrganization || 'Organization not specified'} • 
                                {cert.issueDate ? ` ${cert.issueDate}` : ' Date not specified'}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}

              {/* Add Pagination Controls for Resumes */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                pt: 2,
                borderTop: '1px solid #E0E0E0',
                mt: 2
              }}>
                <TablePagination
                  component="div"
                  count={resumes?.length || 0}
                  page={resumePage}
                  onPageChange={handleResumePageChange}
                  rowsPerPage={resumesPerPage}
                  rowsPerPageOptions={[3]}
                  sx={{
                    '.MuiTablePagination-toolbar': {
                      minHeight: '48px',
                    },
                    '.MuiTablePagination-displayedRows': {
                      fontSize: '0.875rem',
                    },
                    '.MuiTablePagination-selectLabel': {
                      fontSize: '0.875rem',
                    }
                  }}
                />
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            {isFullScreen ? (
              <FullScreenContainer>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  mb: 3,
                  pb: 2,
                  borderBottom: '1px solid #E0E0E0'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      border: '2px solid #fff'
                    }}>
                      <img src={botIcon} alt="bot" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ color: '#131F32', fontWeight: 600, fontSize: '0.875rem' }}>
                        AI Assistant
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: '#64748B', fontSize: '0.75rem' }}>
                        {selectedResume?.entries.find(e => e.key === 'name')?.value || 'No Resume selected'}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    onClick={() => setIsFullScreen(false)}
                    sx={{
                      color: '#159DFF',
                      '&:hover': {
                        backgroundColor: '#E3F2FD'
                      }
                    }}
                  >
                    <FullscreenExitIcon />
                  </IconButton>
                </Box>

                <Box sx={{ 
                  flex: 1, 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3,
                  overflow: 'hidden',
                  minHeight: 0
                }}>
                  <Box sx={{ 
                    flex: 1, 
                    overflowY: 'auto',
                    backgroundColor: '#FAFBFC',
                    borderRadius: '12px',
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    minHeight: 0
                  }}>
                    {messages.map((message) => (
                      <MessageBubble key={message.id} sender={message.sender}>
                        <Box sx={{ position: 'relative' }}>
                          {formatMessageContent(message)}
                          <Typography variant="caption" sx={{ 
                            display: 'block',
                            mt: 1,
                            color: message.sender === 'user' ? 'rgba(255,255,255,0.9)' : '#64748B',
                            fontSize: '0.625rem',
                            textAlign: message.sender === 'user' ? 'right' : 'left',
                            fontWeight: 500
                          }}>
                            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                      </MessageBubble>
                    ))}
                    {isTyping && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1.5,
                        p: '8px 16px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(21, 157, 255, 0.1)',
                        width: 'fit-content',
                        marginLeft: '16px',
                        marginBottom: '8px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #E0E0E0',
                        opacity: 0,
                        animation: 'fadeIn 0.3s ease-in-out forwards',
                        '@keyframes fadeIn': {
                          from: { opacity: 0, transform: 'translateY(10px)' },
                          to: { opacity: 1, transform: 'translateY(0)' }
                        }
                      }}>
                        {/* Animated Dots */}
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 0.5,
                          ml: 0.5
                        }}>
                          <Box sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#159DFF',
                            animation: 'pulse 1.4s ease-in-out infinite',
                            '@keyframes pulse': {
                              '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                              '40%': { opacity: 1, transform: 'scale(1)' }
                            }
                          }} />
                          <Box sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#159DFF',
                            animation: 'pulse 1.4s ease-in-out infinite 0.2s',
                            '@keyframes pulse': {
                              '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                              '40%': { opacity: 1, transform: 'scale(1)' }
                            }
                          }} />
                          <Box sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#159DFF',
                            animation: 'pulse 1.4s ease-in-out infinite 0.4s',
                            '@keyframes pulse': {
                              '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                              '40%': { opacity: 1, transform: 'scale(1)' }
                            }
                          }} />
                        </Box>
                      </Box>
                    )}
                    <div ref={messagesEndRef} />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#64748B', fontSize: '0.75rem', fontWeight: 600 }}>
                      Suggested Questions
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: 1
                    }}>
                      {suggestedQuestions.map((question, index) => (
                        <Chip
                          key={index}
                          label={question.text}
                          icon={question.icon}
                          onClick={() => {
                            setNewMessage(question.text);
                            handleSendMessage();
                          }}
                          sx={{
                            backgroundColor: '#F8F9FA',
                            color: '#1A2027',
                            fontSize: '0.75rem',
                            height: '28px',
                            fontWeight: 500,
                            border: '1px solid #E0E0E0',
                            '& .MuiChip-icon': {
                              fontSize: '14px',
                              color: '#159DFF'
                            },
                            '&:hover': {
                              backgroundColor: '#F0F4F8',
                              borderColor: '#159DFF',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1.5, 
                    alignItems: 'flex-end',
                    backgroundColor: '#FFFFFF',
                    p: 2,
                    borderRadius: '12px',
                    boxShadow: '0 -2px 8px rgba(0,0,0,0.05)'
                  }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      multiline
                      maxRows={4}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: '#FFFFFF',
                          fontSize: '0.875rem',
                          border: '1px solid #E0E0E0',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: '#159DFF',
                            backgroundColor: '#FFFFFF'
                          },
                          '&.Mui-focused': {
                            borderColor: '#159DFF',
                            boxShadow: '0 0 0 2px rgba(21, 157, 255, 0.1)'
                          }
                        }
                      }}
                    />
                    <IconButton 
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim()}
                      size="medium"
                      sx={{
                        backgroundColor: '#159DFF',
                        color: 'white',
                        width: '40px',
                        height: '40px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#0B7AD1',
                          transform: 'scale(1.05)'
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#E3F2FD',
                          color: '#A1A1A1'
                        }
                      }}
                    >
                      <SendIcon sx={{ fontSize: '1.25rem' }} />
                    </IconButton>
                  </Box>
                </Box>
              </FullScreenContainer>
            ) : (
              <Box sx={{ 
                p: 3, 
                borderRadius: '12px',
                height: '600px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  mb: 3,
                  pb: 2,
                  borderBottom: '1px solid #E0E0E0'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      border: '2px solid #fff',
                      backgroundColor: '#F8F9FA'
                    }}>
                      <img src={botIcon} alt="bot" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ color: '#131F32', fontWeight: 600, fontSize: '0.875rem' }}>
                        AI Assistant
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: '#64748B', fontSize: '0.75rem' }}>
                        {selectedResume?.entries.find(e => e.key === 'name')?.value || 'No Resume selected'}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    onClick={() => setIsFullScreen(true)}
                    sx={{
                      color: '#159DFF',
                      '&:hover': {
                        backgroundColor: '#E3F2FD'
                      }
                    }}
                  >
                    <FullscreenIcon />
                  </IconButton>
                </Box>

                <Box sx={{ 
                  flex: 1, 
                  overflowY: 'auto',
                  mb: 2,
                  backgroundColor: '#FAFBFC',
                  borderRadius: '12px',
                  p: 2
                }}>
                  {messages.map((message) => (
                    <MessageBubble key={message.id} sender={message.sender}>
                      <Box sx={{ position: 'relative' }}>
                        {formatMessageContent(message)}
                      </Box>
                    </MessageBubble>
                  ))}
                  {isTyping && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5,
                      p: '8px 16px',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(21, 157, 255, 0.1)',
                      width: 'fit-content',
                      marginLeft: '16px',
                      marginBottom: '8px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      border: '1px solid #E0E0E0',
                      opacity: 0,
                      animation: 'fadeIn 0.3s ease-in-out forwards',
                      '@keyframes fadeIn': {
                        from: { opacity: 0, transform: 'translateY(10px)' },
                        to: { opacity: 1, transform: 'translateY(0)' }
                      }
                    }}>
                      {/* Animated Dots */}
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 0.5,
                        ml: 0.5
                      }}>
                        <Box sx={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#159DFF',
                          animation: 'pulse 1.4s ease-in-out infinite',
                          '@keyframes pulse': {
                            '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                            '40%': { opacity: 1, transform: 'scale(1)' }
                          }
                        }} />
                        <Box sx={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#159DFF',
                          animation: 'pulse 1.4s ease-in-out infinite 0.2s',
                          '@keyframes pulse': {
                            '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                            '40%': { opacity: 1, transform: 'scale(1)' }
                          }
                        }} />
                        <Box sx={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: '#159DFF',
                          animation: 'pulse 1.4s ease-in-out infinite 0.4s',
                          '@keyframes pulse': {
                            '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                            '40%': { opacity: 1, transform: 'scale(1)' }
                          }
                        }} />
                      </Box>
                    </Box>
                  )}
                  <div ref={messagesEndRef} />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 0.5
                  }}>
                    {suggestedQuestions.map((question, index) => (
                      <Chip
                        key={index}
                        label={question.text}
                        icon={question.icon}
                        onClick={() => {
                          setNewMessage(question.text);
                          handleSendMessageResume();
                        }}
                        sx={{
                          backgroundColor: '#F8F9FA',
                          color: '#1A2027',
                          fontSize: '0.75rem',
                          height: '28px',
                          fontWeight: 500,
                          border: '1px solid #E0E0E0',
                          '& .MuiChip-icon': {
                            fontSize: '14px',
                            color: '#159DFF'
                          },
                          '&:hover': {
                            backgroundColor: '#F0F4F8',
                            borderColor: '#159DFF',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      />
                    ))}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessageResume();
                      }
                    }}
                    multiline
                    maxRows={3}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#FFFFFF',
                        fontSize: '0.875rem',
                        border: '1px solid #E0E0E0',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#159DFF',
                          backgroundColor: '#FFFFFF'
                        },
                        '&.Mui-focused': {
                          borderColor: '#159DFF',
                          boxShadow: '0 0 0 2px rgba(21, 157, 255, 0.1)'
                        }
                      }
                    }}
                  />
                  <IconButton 
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim()}
                    size="small"
                    sx={{
                      backgroundColor: '#159DFF',
                      color: 'white',
                      width: '32px',
                      height: '32px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#0B7AD1',
                        transform: 'scale(1.05)'
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#E3F2FD',
                        color: '#A1A1A1'
                      }
                    }}
                  >
                    <SendIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      )}

      {/* Resume Upload Dialog */}
      <Dialog
        open={resumeUploadDialog}
        onClose={() => setResumeUploadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Upload Resume</Typography>
            <IconButton onClick={() => setResumeUploadDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ 
            border: '2px dashed #E0E0E0',
            borderRadius: '8px',
            p: 3,
            textAlign: 'center',
            cursor: uploadLoading ? 'not-allowed' : 'pointer',
            opacity: uploadLoading ? 0.7 : 1,
            '&:hover': { borderColor: uploadLoading ? '#E0E0E0' : '#159DFF' }
          }}>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              id="resume-upload"
              disabled={uploadLoading}
            />
            <label htmlFor="resume-upload">
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                {uploadLoading ? (
                  <CircularProgress size={48} sx={{ color: '#159DFF' }} />
                ) : (
                  <UploadFileIcon sx={{ fontSize: 48, color: '#159DFF' }} />
                )}
                <Typography>
                  {uploadedFile ? uploadedFile.name : 'Drag and drop your resume here, or click to browse'}
                </Typography>
              </Box>
            </label>
          </Box>
          <Typography variant="caption" sx={{ display: 'block', mt: 2, textAlign: 'center', color: 'text.secondary' }}>
            Supported formats: PDF, DOC, DOCX (Max size: 10MB)
          </Typography>
        </DialogContent>
      </Dialog>

      {/* New Job Dialog */}
      <Dialog
        open={newJobDialog}
        onClose={() => setNewJobDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Create New Job</Typography>
            <IconButton onClick={() => setNewJobDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Enter the job description. Include details such as role, responsibilities, requirements, and any other relevant information.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={12}
            placeholder="Enter job description..."
            value={newJobContent}
            onChange={(e) => setNewJobContent(e.target.value)}
            disabled={jobLoading}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setNewJobDialog(false)}
              disabled={jobLoading}
              sx={{ color: '#159DFF', borderColor: '#159DFF' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitNewJob}
              disabled={jobLoading || !newJobContent.trim()}
              sx={{
                backgroundColor: '#159DFF',
                '&:hover': { backgroundColor: '#0B7AD1' }
              }}
            >
              {jobLoading ? <CircularProgress size={24} /> : 'Submit Job'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Add Snackbars at the end of the component */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => handleCloseSnackbar('error')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          severity="error" 
          onClose={() => handleCloseSnackbar('error')}
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => handleCloseSnackbar('success')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          severity="success" 
          onClose={() => handleCloseSnackbar('success')}
          sx={{ width: '100%' }}
        >
          {success}
        </Alert>
      </Snackbar>

      {/* Update loading states in the UI */}
      {searchLoading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          zIndex: 9999
        }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default ResumeRanking; 